/**
 * Quotations list page — org-wide proposal book.
 *
 * 1:1 with the design folder's `quotations.html`:
 *   .projects-header — Title + count caption + "+ New Quote" CTA
 *   .kpi-strip       — 4 tiles: Draft / Sent / Accepted / Total Value
 *   .filter-bar      — All / Draft / Sent / Accepted / Rejected /
 *                      Expired chips + search (via TopbarSearch)
 *   .proj-table      — # · Client · Project · Status · Valid until ·
 *                      Amount · Actions
 *
 * Click a row → /quotations/:id (detail + preview + edit).
 * "+ New Quote" → /quotations/new.
 *
 * All data live from `useQuotations(orgId)`. Server rules already
 * scope reads to org members + the CRM-write gate for actions.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { canCRMWrite } from '@shared/perms';

import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import {
  compactInr,
  formatInr,
  quotationStatusTone,
  type QuotationStatus,
} from '@/lib/quotations';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useQuotations } from '@/lib/useQuotations';

type FilterKey = 'all' | QuotationStatus;

const CHIPS: { key: FilterKey; label: string }[] = [
  { key: 'all',      label: 'All' },
  { key: 'draft',    label: 'Drafts' },
  { key: 'sent',     label: 'Sent' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'expired',  label: 'Expired' },
];

function fmtDate(ts: { toMillis(): number } | null | undefined): string {
  if (!ts) return '—';
  return new Date(ts.toMillis()).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function daysFromNow(ts: { toMillis(): number } | null | undefined): number | null {
  if (!ts) return null;
  return Math.round((ts.toMillis() - Date.now()) / 86400000);
}

export function QuotationsPage() {
  const navigate = useNavigate();
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { quotations, loading } = useQuotations(orgId);
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canCRMWrite(orgRole);

  const [chip, setChip] = useState<FilterKey>('all');
  const { query: search, setPlaceholder } = useTopbarSearch();
  useEffect(() => {
    setPlaceholder('Search quotations by number, client, project…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  // KPI tiles — always from the FULL set, not the filtered set.
  const kpi = useMemo(() => {
    let draft = 0;
    let sent = 0;
    let accepted = 0;
    let rejected = 0;
    let expired = 0;
    let totalValue = 0;
    for (const q of quotations) {
      if (q.status === 'draft') draft += 1;
      else if (q.status === 'sent') sent += 1;
      else if (q.status === 'accepted') accepted += 1;
      else if (q.status === 'rejected') rejected += 1;
      else if (q.status === 'expired') expired += 1;
      totalValue += q.total ?? 0;
    }
    const acceptedValue = quotations
      .filter((q) => q.status === 'accepted')
      .reduce((s, q) => s + (q.total ?? 0), 0);
    const conversionRate =
      sent + accepted + rejected + expired > 0
        ? Math.round((accepted / (sent + accepted + rejected + expired)) * 100)
        : 0;
    return {
      draft,
      sent,
      accepted,
      rejected,
      expired,
      totalValue,
      acceptedValue,
      conversionRate,
    };
  }, [quotations]);

  // Filter + search.
  const filtered = useMemo(() => {
    const q = (search ?? '').trim().toLowerCase();
    return quotations.filter((row) => {
      if (chip !== 'all' && row.status !== chip) return false;
      if (q) {
        const hay = [
          row.number,
          row.clientName,
          row.clientPhone ?? '',
          row.clientEmail ?? '',
          row.projectName ?? '',
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [quotations, chip, search]);

  return (
    <div className="page active" id="page-quotations" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Quotations</h1>
          <div className="projects-stats">
            {quotations.length} total ·{' '}
            {compactInr(kpi.totalValue)} pipeline ·{' '}
            {kpi.conversionRate}% conversion
          </div>
        </div>
        {canWrite ? (
          // Hidden for roles that can't write — Firestore rule blocks it anyway
          <button
            type="button"
            className="btn-primary"
            onClick={() => navigate('/quotations/new')}
            disabled={!orgId}
          >
            <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> New Quote
          </button>
        ) : null}
      </div>

      {/* KPI strip — 4 tiles */}
      <div className="kpi-strip">
        <KpiTile
          label="Drafts"
          value={String(kpi.draft)}
          sub="In progress"
          tone="var(--text-secondary)"
        />
        <KpiTile
          label="Sent"
          value={String(kpi.sent)}
          sub="Awaiting client response"
          tone="#1D4ED8"
        />
        <KpiTile
          label="Accepted"
          value={String(kpi.accepted)}
          sub={compactInr(kpi.acceptedValue) + ' booked'}
          tone="#15803D"
        />
        <KpiTile
          label="Total pipeline"
          value={compactInr(kpi.totalValue)}
          sub={`${kpi.conversionRate}% conversion`}
          tone="var(--text-primary)"
        />
      </div>

      {/* Filter chips */}
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        {CHIPS.map((c) => {
          const count =
            c.key === 'all'
              ? quotations.length
              : c.key === 'draft'
                ? kpi.draft
                : c.key === 'sent'
                  ? kpi.sent
                  : c.key === 'accepted'
                    ? kpi.accepted
                    : c.key === 'rejected'
                      ? kpi.rejected
                      : kpi.expired;
          return (
            <button
              key={c.key}
              type="button"
              className={`filter-chip${chip === c.key ? ' active' : ''}`}
              onClick={() => setChip(c.key)}
            >
              {c.label}
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  color: chip === c.key ? 'var(--brand)' : 'var(--text-tertiary)',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      {loading && quotations.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          Loading quotations…
        </div>
      ) : filtered.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>
            <i className="ph ph-file-text" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>
            {quotations.length === 0
              ? 'No quotations yet'
              : 'No quotations match this filter'}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              maxWidth: 420,
              margin: '0 auto 16px',
            }}
          >
            {quotations.length === 0
              ? 'Send your first quote — set up modules, line items, GST, payment milestones and download a polished PDF in minutes.'
              : 'Try a different status chip or clear the search.'}
          </div>
          {quotations.length === 0 && canWrite ? (
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/quotations/new')}
              disabled={!orgId}
            >
              <i className="ph-bold ph-plus" /> Create your first quote
            </button>
          ) : null}
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="proj-table-wrap">
            <table className="proj-table">
              <thead>
                <tr>
                  <th style={{ width: 130 }}>Quote #</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Status</th>
                  <th>Valid until</th>
                  <th className="right">Amount</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => {
                  const tone = quotationStatusTone(q.status);
                  const validIn = daysFromNow(q.validUntil);
                  const isExpiringSoon =
                    q.status === 'sent' &&
                    validIn != null &&
                    validIn >= 0 &&
                    validIn <= 3;
                  const isOverdue =
                    q.status === 'sent' && validIn != null && validIn < 0;
                  return (
                    <tr
                      key={q.id}
                      onClick={() => navigate(`/quotations/${q.id}`)}
                      className="clickable-row"
                      style={{ cursor: 'pointer' }}
                    >
                      <td
                        style={{
                          fontFamily: 'Menlo, monospace',
                          fontSize: 12,
                          fontWeight: 700,
                          letterSpacing: 0.3,
                        }}
                      >
                        {q.number}
                        {q.version > 0 ? (
                          <span
                            style={{
                              marginLeft: 4,
                              fontSize: 9,
                              padding: '1px 6px',
                              borderRadius: 999,
                              background: '#F1F5F9',
                              color: '#475569',
                              fontWeight: 600,
                            }}
                          >
                            R{q.version}
                          </span>
                        ) : null}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {q.clientName}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'var(--text-tertiary)',
                            marginTop: 2,
                          }}
                        >
                          {q.clientPhone ?? q.clientEmail ?? '—'}
                        </div>
                      </td>
                      <td
                        style={{
                          fontSize: 12,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {q.projectName || '—'}
                      </td>
                      <td>
                        <span
                          className="stage-pill"
                          style={{
                            background: tone.bg,
                            color: tone.fg,
                            fontWeight: 700,
                            fontSize: 10,
                            padding: '2px 8px',
                          }}
                        >
                          {q.status.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 12 }}>
                          {fmtDate(q.validUntil)}
                        </div>
                        {isOverdue ? (
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--critical)',
                              fontWeight: 700,
                              marginTop: 2,
                            }}
                          >
                            EXPIRED {Math.abs(validIn ?? 0)}d AGO
                          </div>
                        ) : isExpiringSoon ? (
                          <div
                            style={{
                              fontSize: 10,
                              color: 'var(--warning)',
                              fontWeight: 700,
                              marginTop: 2,
                            }}
                          >
                            EXPIRES IN {validIn}d
                          </div>
                        ) : null}
                      </td>
                      <td
                        className="right"
                        style={{
                          fontFamily: 'inherit',
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {formatInr(q.total)}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <i
                          className="ph ph-caret-right"
                          style={{ color: 'var(--text-tertiary)' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ───── KPI tile ─────

function KpiTile({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone: string;
}) {
  return (
    <div className="kpi-tile">
      <div className="kpi-label">{label}</div>
      <div
        className="kpi-value tn"
        style={{ color: tone }}
      >
        {value}
      </div>
      <div className="kpi-sub">{sub}</div>
    </div>
  );
}
